import {colord} from 'colord';
import {useAnimatedPopper} from 'platform/components';
import styled from 'styled-components';

import {useEffect, useRef, useState, type FocusEvent, type KeyboardEvent} from 'react';

import {RequiredTestIdProps, suffixTestId, useOutsideClick} from 'shared/web';

import {Button} from './Button';

interface SelectProps extends RequiredTestIdProps {
  options: {
    label: string;
    value: string;
    isSelected?: boolean;
    onClick: () => void;
  }[];
  placeholder: string;
  value: string;
  tabIndex?: number;
}

export function Select(props: SelectProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const {referenceRef, togglePopper, popperProps, Popper, closePopper, isOpen} = useAnimatedPopper({
    placement: 'bottom-start',
    gutter: 4,
    isOutsideClickIgnored: true,
  });

  useEffect(() => {
    if (isOpen) {
      const selectedIndex = props.options.findIndex((option) => option.value === props.value);
      // eslint-disable-next-line react-hooks-js/set-state-in-effect
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [isOpen, props.options, props.value]);

  useOutsideClick({
    ref,
    enabled: isOpen,
    handler: () => {
      closePopper();
    },
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) {
      return;
    }

    const key = event.key;
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();

      const optionsCount = props.options.length;
      if (optionsCount === 0) {
        return;
      }

      if (key === 'ArrowDown') {
        setFocusedIndex((current) => (current + 1) % optionsCount);
      } else if (key === 'ArrowUp') {
        setFocusedIndex((current) => (current - 1 + optionsCount) % optionsCount);
      }
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.stopPropagation();

      const option = props.options.at(focusedIndex);
      if (option) {
        option.onClick();
        closePopper();
      }
    }
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!isOpen) {
      return;
    }

    // If the focus is on the popper (i.e. the option is clicked by mouse), don't close it
    const nextFocusedElement = event.relatedTarget as Node | null;
    if (ref.current && nextFocusedElement && ref.current.contains(nextFocusedElement)) {
      return;
    }

    closePopper();
  };

  return (
    <div ref={ref} onKeyDown={handleKeyDown} onBlur={handleBlur}>
      <div ref={referenceRef}>
        <Button
          isActive={isOpen}
          onClick={togglePopper}
          data-testid={suffixTestId('select', props)}
          title={
            props.options.find((option) => option.value === props.value)?.label ?? props.placeholder
          }
          tabIndex={props.tabIndex}
        />
      </div>
      <Popper {...popperProps}>
        <StyledContent>
          {props.options.map((option, index) => (
            <Button
              key={option.value}
              title={option.label}
              variant="ghost"
              size="small"
              isFullWidth
              justifyContent="start"
              isActive={isOpen ? index === focusedIndex : option.isSelected}
              onClick={() => {
                option.onClick();
                closePopper();
              }}
              tabIndex={-1}
              data-testid={suffixTestId(`selectOption-${option.value}`, props)}
            />
          ))}
        </StyledContent>
      </Popper>
    </div>
  );
}

const StyledContent = styled.div`
  padding: ${({theme}) => theme.getSize(2)};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({theme}) => colord(theme.colors.general.black).alpha(0.7).toRgbString()};
  color: ${({theme}) => theme.colors.general.white};
`;
